import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ITEMS_PER_PAGE = 4;
// Paleta Power BI Moderna
const COLORS = {
  primary: "#0078d4", // Azul Microsoft
  secondary: "#00b7c3", // Teal
  grid: "#e1dfdd",
  text: "#605e5c"
};

const BarChartComponent = ({ data, chartLabel, dataKeys, hide, dataType }) => {
  const [currentPage, setCurrentPage] = useState(0);

  const formatCurrency = (value) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
  const formatNumber = (value) => new Intl.NumberFormat("pt-BR").format(value);
  const formatValue = (value) => dataType === 'currency' ? formatCurrency(value) : formatNumber(value);

  const formatXAxis = (value) => typeof value === 'string' ? value.replace('Semana ', 'S') : value;

  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const isPaginated = totalPages > 1;

  if (currentPage >= totalPages && totalPages > 0) { setCurrentPage(totalPages - 1); }

  const visibleData = useMemo(() => {
      if (!isPaginated) return data;
      const start = currentPage * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE;
      return data.slice(start, end);
  }, [data, currentPage, isPaginated]);

  // Tooltip Estilo Power BI (Fundo escuro ou Branco com sombra forte)
  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="shadow-lg rounded" style={{ backgroundColor: '#fff', border: 'none', padding: '12px', minWidth: '150px' }}>
          <p className="mb-2" style={{ color: '#323130', fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: '4px' }}>{label}</p>
          {payload.map((entry, index) => {
             let displayValue = (dataType === 'number') ? formatNumber(entry.value) : (index === 1 ? formatCurrency(entry.value) : entry.value);
             return (
                 <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                    <span style={{ color: entry.color, fontSize: '0.9rem', marginRight: '10px' }}>● {entry.name}:</span>
                    <span style={{ fontWeight: 600, color: '#201f1e' }}>{displayValue}</span>
                 </div>
             );
          })}
        </div>
      );
    }
    return null;
  };

  const hasSecondKey = dataKeys.length > 1;

  return (
    <div className="d-flex flex-column align-items-center w-100 bg-white rounded p-2">
        <ResponsiveContainer className="" width="100%" height={400}>
        <BarChart data={visibleData} margin={{ top: 20, right: 30, left: 10, bottom: 50 }}>
            {/* Grid mais suave, apenas horizontal */}
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
            
            <XAxis 
                dataKey="DESCRICAO1" 
                {...(hide ? { hide: true } : {})} 
                interval={0} 
                tickFormatter={formatXAxis} 
                tick={{ fontSize: 12, fill: COLORS.text, angle: -45, textAnchor: 'end' }} 
                height={60}
                axisLine={{ stroke: COLORS.grid }}
                tickLine={false}
            />
            
            <YAxis 
                tickFormatter={formatNumber} 
                yAxisId="left" orientation="left" 
                tick={{ fontSize: 12, fill: COLORS.text }} 
                axisLine={false} 
                tickLine={false}
            />
            
            {hasSecondKey && (
                <YAxis 
                    tickFormatter={formatValue} 
                    yAxisId="right" orientation="right" 
                    tick={{ fontSize: 12, fill: COLORS.text }} 
                    axisLine={false} 
                    tickLine={false}
                />
            )}

            <Tooltip content={customTooltip} cursor={{fill: '#f3f2f1'}} />
            <Legend verticalAlign="top" height={36} iconType="circle"/>
            
            {/* Barras com Radius e cores Power BI */}
            <Bar yAxisId="left" dataKey={dataKeys[0]} fill={COLORS.primary} name={chartLabel[0]} radius={[4, 4, 0, 0]} barSize={40} />
            
            {hasSecondKey && (
                <Bar yAxisId="right" dataKey={dataKeys[1]} fill={COLORS.secondary} name={chartLabel[1]} radius={[4, 4, 0, 0]} barSize={40} />
            )}
        </BarChart>
        </ResponsiveContainer>

        {isPaginated && (
            <div className="d-flex justify-content-center align-items-center mt-2 gap-2">
                <button className="btn btn-sm btn-light border" onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>←</button>
                <span className="small text-muted fw-bold">Pág {currentPage + 1}/{totalPages}</span>
                <button className="btn btn-sm btn-light border" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage === totalPages - 1}>→</button>
            </div>
        )}
    </div>
  );
};

export default BarChartComponent;